/**
 * guide/feedback 전체 결과 피드백 island (NSM source).
 *
 * Mount: `<section data-mount="feedback" data-service-slug="..." data-guide-slug="...">`
 *   - `<div data-feedback-buttons>`     3개 result 버튼
 *   - `<div data-feedback-note hidden>` partial/fail 클릭 시 노출:
 *       textarea + [보내기] [건너뛰기]
 *   - `<div data-feedback-summary hidden>` 최종:
 *       `<div data-feedback-status>`  ("고맙습니다 ✓" / "이번 주에 이미 답하셨어요")
 *       `<div data-feedback-stats>`   ("✓7 · ~1 · ✕2 (성공률 87%)")
 *
 * Flow:
 *   - success → 즉시 POST → summary
 *   - partial → buttons 숨김, note 노출 ("어떤 점이 어려웠나요?"). [보내기]→POST(note)→summary, [건너뛰기]→POST(no note)→summary
 *   - fail    → buttons 숨김, note 노출 ("어디서 막혔나요?"). 같은 흐름
 *
 * 정책 (guide/vote 와 통일): ISO week 단위 1회. 다음 주가 시작되면 mount-time
 * GET이 already_voted_result=null 반환 → buttons 다시 노출되어 재응답 가능.
 *
 * Bias guard: counts NEVER shown before vote. Mount-time GET only checks
 * already_voted_result; if set, jumps straight to summary.
 */

import { getOrCreateAnonId } from "./anonymous-id";
import { updateLastResult } from "./recent-guides";

type Bound = { serviceSlug: string; guideSlug: string; root: HTMLElement };
type Counts = { success_count: number; partial_count: number; fail_count: number };
type GetResponse = Counts & { already_voted_result: "success" | "partial" | "fail" | null };
type PostResponse = Counts & { inserted: boolean };
type Result = "success" | "partial" | "fail";

const NOTE_PROMPT: Record<Result, string> = {
  success: "",
  partial: "어떤 점이 어려웠나요? (선택, 보강에 사용됩니다)",
  fail: "어디서 막혔나요? (선택, 운영자가 확인합니다)",
};

function readBinding(el: HTMLElement): Bound | null {
  const serviceSlug = el.dataset.serviceSlug;
  const guideSlug = el.dataset.guideSlug;
  if (!serviceSlug || !guideSlug) return null;
  return { serviceSlug, guideSlug, root: el };
}

function setStatus(root: HTMLElement, text: string): void {
  const target = root.querySelector<HTMLElement>("[data-feedback-status]");
  if (target) target.textContent = text;
}

function formatStats(c: Counts): string {
  const total = c.success_count + c.partial_count + c.fail_count;
  if (total === 0) return "지금까지 응답 없음";
  const pct = Math.round((100 * c.success_count) / total);
  return `지금까지 ✓${c.success_count} · ~${c.partial_count} · ✕${c.fail_count} (성공률 ${pct}%)`;
}

function setStats(root: HTMLElement, c: Counts): void {
  const target = root.querySelector<HTMLElement>("[data-feedback-stats]");
  if (target) target.textContent = formatStats(c);
}

function showSummary(root: HTMLElement): void {
  const buttons = root.querySelector<HTMLElement>("[data-feedback-buttons]");
  const note = root.querySelector<HTMLElement>("[data-feedback-note]");
  const summary = root.querySelector<HTMLElement>("[data-feedback-summary]");
  if (buttons) buttons.hidden = true;
  if (note) note.hidden = true;
  if (summary) summary.hidden = false;
}

function showAfterPanel(root: HTMLElement, result: Result): void {
  for (const panel of root.querySelectorAll<HTMLElement>("[data-feedback-after]")) {
    panel.hidden = panel.dataset.feedbackAfter !== result;
  }
}

function lockSelected(root: HTMLElement, chosen: HTMLButtonElement): void {
  for (const b of root.querySelectorAll<HTMLButtonElement>(
    "[data-feedback-buttons] button[data-result]",
  )) {
    const isChosen = b === chosen;
    b.dataset.active = isChosen ? "true" : "false";
    b.setAttribute("aria-pressed", isChosen ? "true" : "false");
    b.disabled = !isChosen;
  }
}

function unlockAll(root: HTMLElement): void {
  for (const b of root.querySelectorAll<HTMLButtonElement>(
    "[data-feedback-buttons] button[data-result]",
  )) {
    delete b.dataset.active;
    b.removeAttribute("aria-pressed");
    b.disabled = false;
  }
}

function showNotePrompt(root: HTMLElement, prompt: string): void {
  const buttons = root.querySelector<HTMLElement>("[data-feedback-buttons]");
  const note = root.querySelector<HTMLElement>("[data-feedback-note]");
  if (buttons) buttons.hidden = true;
  if (note) note.hidden = false;
  const promptEl = root.querySelector<HTMLElement>("[data-feedback-note-prompt]");
  if (promptEl) promptEl.textContent = prompt;
  const ta = root.querySelector<HTMLTextAreaElement>("[data-feedback-note-input]");
  if (ta) ta.value = "";
}

async function checkAlreadyVoted(bound: Bound): Promise<void> {
  const url = `/api/feedback?anon_id=${encodeURIComponent(getOrCreateAnonId())}&service_slug=${encodeURIComponent(bound.serviceSlug)}&guide_slug=${encodeURIComponent(bound.guideSlug)}`;
  const res = await fetch(url, { method: "GET" });
  if (!res.ok) return;
  const data = (await res.json()) as GetResponse;
  if (data.already_voted_result !== null) {
    setStatus(bound.root, "이번 주에 이미 답하셨어요 (다음 주 다시 가능)");
    setStats(bound.root, data);
    showSummary(bound.root);
    showAfterPanel(bound.root, data.already_voted_result);
  }
}

async function castVote(bound: Bound, result: Result, note: string | undefined): Promise<boolean> {
  const body: Record<string, unknown> = {
    anon_id: getOrCreateAnonId(),
    service_slug: bound.serviceSlug,
    guide_slug: bound.guideSlug,
    result,
  };
  if (note && note.length > 0) body.note = note;

  const res = await fetch("/api/feedback", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (res.status === 429) {
    setStatus(bound.root, "잠시 후 다시 시도해주세요 (rate-limit)");
    return false;
  }
  if (!res.ok) {
    setStatus(bound.root, `서버 오류 (${res.status}) — 잠시 후 다시 시도해주세요`);
    return false;
  }
  const data = (await res.json()) as PostResponse;
  setStatus(
    bound.root,
    data.inserted ? "고맙습니다 ✓" : "이번 주에 이미 답하셨어요 (다음 주 다시 가능)",
  );
  setStats(bound.root, data);
  showSummary(bound.root);
  showAfterPanel(bound.root, result);
  // landing/recent-guides — 직전 결과 케이스 분리용 LocalStorage 갱신.
  updateLastResult(bound.serviceSlug, bound.guideSlug, result);
  return true;
}

export function mountFeedback(root: HTMLElement): void {
  const maybe = readBinding(root);
  if (!maybe) return;
  // Capture as non-null for inner closures (TS control-flow doesn't always
  // propagate through closures created later in scope).
  const bound: Bound = maybe;

  // Mount-time GET: hide buttons immediately if already voted.
  void checkAlreadyVoted(bound).catch(() => {
    /* silent — buttons remain shown */
  });

  // Result buttons. success → instant POST. partial/fail → show note prompt.
  for (const btn of root.querySelectorAll<HTMLButtonElement>(
    "[data-feedback-buttons] button[data-result]",
  )) {
    const result = btn.dataset.result;
    if (result !== "success" && result !== "partial" && result !== "fail") continue;
    btn.addEventListener("click", async () => {
      lockSelected(bound.root, btn);
      if (result === "success") {
        let ok = false;
        try {
          ok = await castVote(bound, result, undefined);
        } catch {
          setStatus(bound.root, "네트워크 오류 — 잠시 후 다시 시도해주세요");
        }
        if (!ok) unlockAll(bound.root);
        return;
      }
      // partial / fail — show note textarea, defer POST.
      // fail 은 issue 수집이 본 의도 — [건너뛰기] 숨기고 textarea 필수.
      btn.dataset.chosen = "1";
      const skipEl = bound.root.querySelector<HTMLElement>("[data-feedback-note-skip]");
      if (skipEl) skipEl.hidden = result === "fail";
      showNotePrompt(bound.root, NOTE_PROMPT[result]);
    });
  }

  // Note panel buttons: 보내기 / 건너뛰기
  const sendBtn = root.querySelector<HTMLButtonElement>("[data-feedback-note-send]");
  const skipBtn = root.querySelector<HTMLButtonElement>("[data-feedback-note-skip]");
  const ta = root.querySelector<HTMLTextAreaElement>("[data-feedback-note-input]");

  function pickChosenResult(): Result | null {
    const chosen = root.querySelector<HTMLButtonElement>('button[data-result][data-chosen="1"]');
    const r = chosen?.dataset.result;
    if (r === "partial" || r === "fail") return r;
    return null;
  }

  function setNoteError(text: string): void {
    const el = bound.root.querySelector<HTMLElement>("[data-feedback-note-error]");
    if (el) el.textContent = text;
  }

  async function submitWithNote(noteText: string | undefined): Promise<void> {
    const r = pickChosenResult();
    if (!r) return;
    // fail 은 issue 강제 — 빈 텍스트면 POST 안 함, inline error 노출.
    if (r === "fail" && (!noteText || noteText.length === 0)) {
      setNoteError("어디서 막혔는지 알려주세요 (필수)");
      return;
    }
    if (sendBtn) sendBtn.disabled = true;
    if (skipBtn) skipBtn.disabled = true;
    setNoteError("");
    let ok = false;
    try {
      ok = await castVote(bound, r, noteText);
    } catch {
      setStatus(bound.root, "네트워크 오류 — 잠시 후 다시 시도해주세요");
    }
    if (!ok) {
      // note 패널이 아직 보이고 summary는 hidden이라 setStatus 메시지가 안 보인다 —
      // note 패널 안 inline error에도 박아 silent-fail 차단.
      const statusText =
        bound.root.querySelector<HTMLElement>("[data-feedback-status]")?.textContent ?? "";
      setNoteError(statusText || "보내기 실패 — 잠시 후 다시 시도해주세요");
      if (sendBtn) sendBtn.disabled = false;
      if (skipBtn) skipBtn.disabled = false;
    }
  }

  if (sendBtn) {
    sendBtn.addEventListener("click", async () => {
      const text = ta?.value.trim() ?? "";
      await submitWithNote(text.length > 0 ? text : undefined);
    });
  }
  if (skipBtn) {
    skipBtn.addEventListener("click", async () => {
      await submitWithNote(undefined);
    });
  }
}
